import { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useStore } from '@/hooks/useStore';
import { useTelegram } from '@/providers/TelegramProvider';
import ErrorBoundary from '@/components/ErrorBoundary';
import HomePage from '@/pages/HomePage';
import PlantPage from '@/pages/PlantPage';
import AddPlantPage from '@/pages/AddPlantPage';
import Navigation from '@/components/Navigation';
import LoadingSpinner from '@/components/LoadingSpinner';
import OnboardingModal from '@/components/OnboardingModal';

// Lazy-loaded pages
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const ArchivePage = lazy(() => import('@/pages/ArchivePage'));
const StatsPage = lazy(() => import('@/pages/StatsPage'));
const SpeciesDetailPage = lazy(() => import('@/pages/SpeciesDetailPage'));
const CatalogPage = lazy(() => import('@/pages/CatalogPage'));

const ONBOARDING_KEY = 'stoneflower_onboarding_seen';

function App() {
  const { isReady } = useTelegram();
  const { fetchUser, fetchPlants, fetchUnreadCounts, isLoadingUser, isLoadingPlants, user, plants, error, clearError } = useStore();
  const [showOnboarding, setShowOnboarding] = useState(() => {
    return !localStorage.getItem(ONBOARDING_KEY);
  });

  const handleOnboardingComplete = () => {
    localStorage.setItem(ONBOARDING_KEY, '1');
    setShowOnboarding(false);
  };

  useEffect(() => {
    if (!isReady) return;

    // Fetch initial data (species loaded lazily on catalog/add pages)
    fetchUser();
    fetchPlants();
    fetchUnreadCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady]);

  // Wait for Telegram to be ready
  if (!isReady) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Show loading state only on initial load, not on subsequent refreshes
  const isInitialLoading = (isLoadingUser && !user) || (isLoadingPlants && plants.length === 0);
  if (isInitialLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (showOnboarding) {
    return <OnboardingModal onComplete={handleOnboardingComplete} />;
  }

  return (
    <ErrorBoundary>
    <BrowserRouter>
      {/* Error toast — glass style */}
      {error && (
        <div className="fixed top-4 left-4 right-4 z-50 px-4 py-3 rounded-xl"
          style={{
            background: '#ef4444',
            border: '1px solid #dc2626',
            boxShadow: '0 4px 16px rgba(239, 68, 68, 0.3)',
            color: 'white',
          }}
        >
          <div className="flex items-center justify-between">
            <span>{error}</span>
            <button onClick={clearError} className="ml-4 font-bold opacity-80 hover:opacity-100">×</button>
          </div>
        </div>
      )}

      <div className="min-h-screen flex flex-col pb-20">
        <Suspense fallback={<div className="flex-1 flex items-center justify-center"><LoadingSpinner size="lg" /></div>}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/plant/:id" element={<PlantPage />} />
            <Route path="/add" element={<AddPlantPage />} />
            <Route path="/edit/:id" element={<AddPlantPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/archive" element={<ArchivePage />} />
            <Route path="/catalog" element={<CatalogPage />} />
            <Route path="/stats" element={<StatsPage />} />
            <Route path="/species/:id" element={<SpeciesDetailPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>

        <Navigation />
      </div>
    </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
